// Outbox dead letters (FR-70): internal messages of the app or vault database
// that ran out of retries. Payloads are never shown; an administrator can
// queue one again once the cause is fixed. Webhook deliveries to companies
// have their own page (AdminDeadLettersPage).
import { Button, Code, Group, Loader, SegmentedControl, Stack, Table, Text } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";

type Source = "app" | "vault";
type DeadLetter = Schemas["DeadLetter"];

export function AdminOutboxPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("admin_outbox.intro")}>
      <Outbox />
    </AdminLayout>
  );
}

function Outbox() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<Source>("app");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = ["admin", "outbox", source] as const;
  const dead = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/admin/outbox/dead-letters", { params: { query: { source, limit: 200 } } })),
  });
  const items = dead.data?.items ?? [];

  const retry = async (d: DeadLetter) => {
    setError(null);
    setNotice(null);
    const { response, error } = await api.POST("/admin/outbox/{source}/{message_id}/retry", {
      params: { path: { source: d.source, message_id: d.id } },
    });
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("admin_outbox.retried", { kind: d.kind }));
    void queryClient.invalidateQueries({ queryKey: ["admin", "outbox"] });
  };

  return (
    <Stack gap="md">
      <Group gap="sm">
        <Text size="sm" id="outbox-source">
          {t("admin_outbox.source")}
        </Text>
        <SegmentedControl
          size="xs"
          aria-labelledby="outbox-source"
          value={source}
          onChange={(v) => {
            setNotice(null);
            setError(null);
            setSource(v as Source);
          }}
          data={[
            { value: "app", label: t("admin_audit.source_app") },
            { value: "vault", label: t("admin_audit.source_vault") },
          ]}
        />
      </Group>
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {dead.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : dead.isError ? (
        <FormError message={errorMessage(dead.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="outbox-empty">
          {t("admin_outbox.empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={680}>
          <Table striped verticalSpacing="sm" aria-label={t("admin.tab_outbox")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin_outbox.kind")}</Table.Th>
                <Table.Th>{t("admin_outbox.attempts")}</Table.Th>
                <Table.Th>{t("admin_outbox.last_error")}</Table.Th>
                <Table.Th>{t("admin_outbox.dead_at")}</Table.Th>
                <Table.Th>{t("admin_companies.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((d) => (
                <Table.Tr key={d.id} data-testid="outbox-row">
                  <Table.Td>
                    <Code>{d.kind}</Code>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {d.id}
                    </Text>
                  </Table.Td>
                  <Table.Td>{d.attempts}</Table.Td>
                  <Table.Td>
                    <Text size="xs" style={{ wordBreak: "break-word" }}>
                      {d.last_error || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>{f.dateTime(d.dead_lettered_at)}</Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => void retry(d)}
                      aria-label={t("admin_outbox.retry_for", { kind: d.kind, date: f.dateTime(d.dead_lettered_at) })}
                    >
                      {t("admin_outbox.retry")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
